/**
 * FollowUpEmailModal — Post-hangup AI-drafted email referencing what the
 * prospect actually said. One-click "Send" or copy-to-clipboard.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, X, Copy, Sparkles, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function FollowUpEmailModal({ open, onClose, transcript, lead, disposition, tone = "warm" }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState({ subject: "", body: "" });
  const [edited, setEdited] = useState(false);
  const [currentTone, setCurrentTone] = useState(tone);
  const [sending, setSending] = useState(false);

  const draft = useCallback(async (toneOverride) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/dialer/follow-up-email`, {
        transcript: transcript || [],
        lead: lead || null,
        disposition: disposition || null,
        tone: toneOverride || currentTone,
      }, { headers: authHeaders() });
      setEmail({ subject: data.subject || "", body: data.body || "" });
      setEdited(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft email");
    } finally {
      setLoading(false);
    }
  }, [transcript, lead, disposition, currentTone]);

  useEffect(() => { if (open) draft(); /* eslint-disable-next-line */ }, [open]);

  const copyEmail = () => {
    const txt = `Subject: ${email.subject}\n\n${email.body}`;
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  const sendEmail = async () => {
    if (!lead?.id) { toast.error("Lead context missing — copy to clipboard instead"); return; }
    setSending(true);
    try {
      // Best-effort send via existing communications endpoint when present.
      // Falls back to a clear message if no endpoint exists yet.
      await axios.post(`${API}/communications/email/send`, {
        lead_id: lead.id,
        subject: email.subject, body: email.body,
      }, { headers: authHeaders() });
      toast.success("Email sent");
      onClose?.();
    } catch (e) {
      const msg = e?.response?.status === 404
        ? "Email send endpoint not yet wired — use Copy + paste into your mail client"
        : (e?.response?.data?.detail || "Send failed");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div data-testid="follow-up-email-modal" style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
      backdropFilter: "blur(4px)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: 720,
        boxShadow: "0 24px 60px rgba(0,0,0,0.25)", overflow: "hidden",
        fontFamily: "inherit",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: "0.5px solid var(--border, #e2e8f0)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Mail style={{ width: 16, height: 16, color: "#2563eb" }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>AI-drafted follow-up email</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
                Personalized from your call transcript and disposition
              </div>
            </div>
          </div>
          <button data-testid="follow-up-close" onClick={onClose} style={{
            padding: 6, background: "transparent", border: 0, cursor: "pointer", color: "#64748b",
          }}><X style={{ width: 16, height: 16 }} /></button>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 6 }}>
            {["warm", "formal"].map((t) => (
              <button key={t} data-testid={`follow-up-tone-${t}`} onClick={() => { setCurrentTone(t); draft(t); }} disabled={loading} style={{
                padding: "5px 10px", fontSize: 11, fontWeight: 600,
                background: currentTone === t ? "var(--navy, #0f172a)" : "#f1f5f9",
                color: currentTone === t ? "#fff" : "#475569",
                border: 0, borderRadius: 999, cursor: loading ? "not-allowed" : "pointer",
                textTransform: "capitalize", fontFamily: "inherit",
              }}>{t}</button>
            ))}
            <button data-testid="follow-up-redraft" onClick={() => draft()} disabled={loading} style={{
              marginLeft: "auto", padding: "5px 10px", fontSize: 11, fontWeight: 600,
              background: "transparent", color: "#5B6CFF", border: 0, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit",
            }}>
              {loading ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
              {loading ? "Drafting…" : "Re-draft"}
            </button>
          </div>

          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Subject</label>
            <input
              data-testid="follow-up-subject"
              value={email.subject}
              onChange={(e) => { setEmail((s) => ({ ...s, subject: e.target.value })); setEdited(true); }}
              disabled={loading}
              style={{
                width: "100%", padding: "8px 10px", fontSize: 13, fontWeight: 500,
                border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 8,
                marginTop: 4, fontFamily: "inherit", color: "#0f172a",
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Body {edited && <span style={{ color: "#92400e", marginLeft: 4 }}>(edited)</span>}
            </label>
            <textarea
              data-testid="follow-up-body"
              value={email.body}
              onChange={(e) => { setEmail((s) => ({ ...s, body: e.target.value })); setEdited(true); }}
              disabled={loading}
              rows={12}
              style={{
                width: "100%", padding: 10, fontSize: 12, lineHeight: 1.55,
                border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 8,
                marginTop: 4, fontFamily: "inherit", color: "#0f172a", resize: "vertical",
                whiteSpace: "pre-wrap",
              }}
            />
          </div>
        </div>

        <div style={{
          padding: "12px 20px", borderTop: "0.5px solid var(--border, #e2e8f0)",
          background: "#f8fafc", display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <button data-testid="follow-up-copy" onClick={copyEmail} disabled={loading || !email.body} style={{
            padding: "8px 12px", fontSize: 12, fontWeight: 600,
            background: "#fff", color: "#475569",
            border: "0.5px solid var(--border)", borderRadius: 8, cursor: loading ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
          }}><Copy style={{ width: 12, height: 12 }} /> Copy</button>
          <button data-testid="follow-up-send" onClick={sendEmail} disabled={sending || loading || !email.body} style={{
            padding: "8px 14px", fontSize: 12, fontWeight: 600,
            background: "#2563eb", color: "#fff",
            border: 0, borderRadius: 8, cursor: (sending || loading) ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
          }}>
            {sending ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> : <Send style={{ width: 12, height: 12 }} />}
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
